import { Injectable } from '@angular/core';
import {
  ReportSection,
  ReportSectionConfigForDraggableItem,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationWithOrder,
  ReportSectionDataSection,
  ReportSectionPlacementType,
  TemplateDesignerSelectedReportState
} from '@capital-access/reporting/common';
import {
  ActivityHistoryConfiguration,
  ActivityIndicatorsConfiguration,
  CombinedOwnershipConfiguration,
  DisclosureConfiguration,
  DRPriceVolumeConfiguration,
  EquityPortfolioConfiguration,
  FundsSummaryConfiguration,
  HistoricalOwnershipConfiguration,
  MonthlyDrActivityConfiguration,
  PeerAndIndustryOwnershipConfiguration,
  SustainabilityFitMetricConfiguration,
  TargetingConfiguration,
  YTDPriceVolumeConfiguration
} from '@capital-access/reporting/report-section';

@Injectable()
export class ConfigurationPanelService {
  public static ConvertReportSectionsToDraggableList(
    sections: ReportSectionConfigurationWithOrder[]
  ): ReportSectionConfigForDraggableItem[] {
    return sections.map(section => {
      const updatedConfig = section.config;
      const adjustedWidth = section.width
        ? Math.min(Math.max(section.width, updatedConfig.minSize), updatedConfig.maxSize)
        : section.width;
      return {
        order: section.order,
        name: updatedConfig.name,
        minSize: updatedConfig.minSize,
        maxSize: updatedConfig.maxSize,
        disabledLayoutWidthControl: updatedConfig?.disabledLayoutWidthControl,
        disableSectionSelection: updatedConfig?.disableSectionSelection,
        displayName: updatedConfig?.displayName || '',
        groupName: updatedConfig?.groupName,
        totalDataSectionsCount: updatedConfig?.totalDataSectionsCount,
        selectedDataSectionsCount: updatedConfig?.selectedDataSectionsCount,
        width: adjustedWidth,
        checkBoxState: section.isSelected,
        isParentItem: true,
        canSelectAllDataSections: updatedConfig.canSelectAllDataSections,
        maxGridUnitsAllowed: updatedConfig.maxGridUnitsAllowed,
        noCommentsReordering: updatedConfig.name === 'enableItineraryCommentary',
        noActivityHeaderReordering: updatedConfig.name === 'activityHeader',
        noActivityAttributesReordering: updatedConfig.name === 'activityAttributes',
        noParticipantInfoReordering: updatedConfig.name === 'participantsInfo',
        activityHeaderTurnOff: updatedConfig.name === 'activityHeader',
        participantInfoTurnOff: updatedConfig.name === 'participantsInfo',
        noDataPointsAvailable: updatedConfig.name === 'enableCommentary' || updatedConfig.totalDataSectionsCount === 0,
        disabled: updatedConfig?.placementType == ReportSectionPlacementType.Static,
        isStatic: updatedConfig?.placementType == ReportSectionPlacementType.Static,
        totalColumnsToDisplay: updatedConfig?.totalColumnsToDisplay
      } as ReportSectionConfigForDraggableItem;
    });
  }

  public static IsReportSectionConfigWidthValid(
    width: number,
    reportSection: ReportSectionConfigurationWithOrder
  ): boolean {
    return width >= reportSection.config.minSize && width <= reportSection.config.maxSize;
  }

  public static GetWidthErrorMessage(section: ReportSectionConfigurationWithOrder): string {
    return `Width must be between ${section.config.minSize} and ${section.config.maxSize}`;
  }

  public static ReportSectionCheckboxToggle(
    checked: boolean,
    reportSections: ReportSectionConfigurationWithOrder[],
    reportSectionName: string,
    checkAll = false
  ): ReportSectionConfigurationWithOrder[] {
    return reportSections.map(section => {
      if (section.config.disableSectionSelection === true) {
        return section;
      }
      if (checkAll || section.config.name === reportSectionName) {
        section = {
          ...section,
          isSelected:
            section.config.reportSection === ReportSection.Disclosure ||
            section.config.reportSection === ReportSection.BnyHeader
              ? true
              : checked
        };
      }
      return section;
    });
  }

  public static FindSelectedReportSectionConfig(
    reportSections: ReportSectionConfigurationWithOrder[],
    selectedReport: ReportSectionConfigForDraggableItem
  ): TemplateDesignerSelectedReportState | null {
    const reportSectionConfig = reportSections.find(section => section.config.name === selectedReport?.name);
    return getTemplateDesignerSelectedReportState(reportSectionConfig);
  }

  public static PrepareDraggableItemsConfig(
    sectionConfig: ReportSectionConfigurationWithOrder
  ): Partial<ReportSectionConfigForDraggableItem>[] {
    const draggableItem = {
      name: sectionConfig?.config.name,
      isStatic: true,
      checkBoxState: sectionConfig.isSelected,
      order: sectionConfig.order,
      displayName: sectionConfig.config.displayName,
      isParentItem: true,
      width: sectionConfig.width,
      reportSection: sectionConfig.config.reportSection,
      minSize: sectionConfig.config.minSize,
      maxSize: sectionConfig.config.maxSize,
      totalDataSectionsCount: sectionConfig.config?.totalDataSectionsCount,
      selectedDataSectionsCount: sectionConfig.config.selectedDataSectionsCount,
      canSelectAllDataSections: sectionConfig.config.canSelectAllDataSections,
      maxGridUnitsAllowed: sectionConfig.config.maxGridUnitsAllowed,
      totalColumnsToDisplay: sectionConfig.config.totalColumnsToDisplay,
      nestedItems: sectionConfig.config.availableDataSections.map((section: ReportSectionDataSection) => {
        const subSections =
          section.subSections?.map(subSection => {
            return {
              name: subSection.name,
              displayName: subSection.displayName,
              groupName: subSection.groupName,
              disabled: !section.canReorderSubSections,
              checkBoxState: subSection.isSelected,
              isParentItem: false,
              iconClass: subSection.icon
            } as Partial<ReportSectionConfigForDraggableItem>;
          }) || [];
        return {
          name: section.name,
          displayName: section.displayName,
          groupName: section.groupName,
          checkBoxState: section.isSelected,
          isParentItem: false,
          canReorderSubSections: section.canReorderSubSections || false,
          nestedItems: subSections,
          subsections: subSections,
          iconClass: section.icon
        } as ReportSectionConfigForDraggableItem;
      })
    };
    return [draggableItem];
  }

  public static UpdateReportSectionOrder(
    draggableItems: ReportSectionConfigForDraggableItem[],
    reportSections: ReportSectionConfigurationWithOrder[]
  ): ReportSectionConfigurationWithOrder[] {
    const orderMap = new Map(draggableItems.map((item, index) => [item.name, index]));
    const mappedSections = reportSections.map((section: ReportSectionConfigurationWithOrder) => {
      const changedOrder = orderMap.get(section.config.name);

      return {
        ...section,
        order: changedOrder !== undefined ? changedOrder : section.order
      } as ReportSectionConfigurationWithOrder;
    });

    const sorted = mappedSections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return sorted;
  }

  public static ReportSectionDataPointSelectClearAll(
    checked: boolean,
    reportSection: ReportSectionConfigurationWithOrder
  ): ReportSectionConfigurationWithOrder {
    const updatedAvailableDataSections = reportSection.config.availableDataSections.map(section => ({
      ...section,
      isSelected: section.canConfigure && !section.isStatic ? checked : section.isSelected,
      subSections: section.subSections?.map(subSection => ({
        ...subSection,
        isSelected: subSection.canConfigure && !subSection.isStatic ? checked : subSection.isSelected
      }))
    }));

    const selectedCount = updatedAvailableDataSections.reduce((count, section) => {
      if (section.isSelected) count++;
      section.subSections?.forEach(sub => {
        if (sub.isSelected) count++;
      });
      return count;
    }, 0);

    const updatedConfig = {
      ...reportSection.config,
      availableDataSections: updatedAvailableDataSections,
      selectedDataSectionsCount: checked ? selectedCount : 0
    };

    const updatedReportsection = {
      ...reportSection,
      isSelected: checked && selectedCount > 0,
      config: updatedConfig as ReportSectionConfigurationBase
    };

    const { maxSize, minSize } = ConfigurationPanelService.GetUpdatedSizes(updatedReportsection);
    updatedReportsection.config.minSize = minSize;
    updatedReportsection.config.maxSize = maxSize;

    return updatedReportsection;
  }

  private static CalculateTotalSelectedUnits(sections: ReportSectionDataSection[]): number {
    return sections.reduce((acc, section: ReportSectionDataSection) => {
      const sectionUnits = section.isSelected ? section.unit || 0 : 0;
      const subSectionUnits =
        section.subSections?.filter(sub => sub.isSelected).reduce((subAcc, sub) => subAcc + (sub.unit || 0), 0) || 0;
      return acc + sectionUnits + subSectionUnits;
    }, 0);
  }

  private static CanUpdateDataSectionUnit(
    targetSection: ReportSectionDataSection,
    reportSection: ReportSectionConfigurationWithOrder,
    maxGridUnitsAllowed: number,
    checked: boolean
  ): boolean {
    const totalSelectedUnits = ConfigurationPanelService.CalculateTotalSelectedUnits(
      reportSection.config.availableDataSections
    );
    const targetUnits = targetSection?.unit || 0;

    if (checked) {
      return totalSelectedUnits + targetUnits <= maxGridUnitsAllowed;
    }
    return totalSelectedUnits - targetUnits > 0;
  }

  //  add list of config classes that needs minSize property change on data section toggle
  private static getConfigurationInstanceFromConfigClass(
    reportSection: ReportSectionConfigurationWithOrder
  ): ReportSectionConfigurationBase | undefined {
    const configClass = reportSection.configClass as unknown as string;
    switch (configClass) {
      case 'FundsSummaryConfiguration':
        return new FundsSummaryConfiguration();
      case 'EquityPortfolioConfiguration':
        return new EquityPortfolioConfiguration();
      case 'TargetingConfiguration':
        return new TargetingConfiguration();
      case 'CombinedOwnershipConfiguration':
        return new CombinedOwnershipConfiguration();
      case 'PeerAndIndustryOwnershipConfiguration':
        return new PeerAndIndustryOwnershipConfiguration();
      case 'HistoricalOwnershipConfiguration':
        return new HistoricalOwnershipConfiguration();
      case 'ActivityHistoryConfiguration':
        return new ActivityHistoryConfiguration();
      case 'SustainabilityFitMetricConfiguration':
        return new SustainabilityFitMetricConfiguration();
      case 'ActivityIndicatorsConfiguration':
        return new ActivityIndicatorsConfiguration();
      case 'MonthlyDrActivityConfiguration':
        return new MonthlyDrActivityConfiguration();
      case 'DRPriceVolumeConfiguration':
        return new DRPriceVolumeConfiguration();
      case 'YTDPriceVolumeConfiguration':
        return new YTDPriceVolumeConfiguration();
      case 'DisclosureConfiguration':
        return new DisclosureConfiguration();
      default:
        return undefined;
    }
  }
  private static getUpdatedConfiguration(
    reportSection: ReportSectionConfigurationWithOrder
  ): ReportSectionConfigurationBase | undefined {
    const configurationInstance = ConfigurationPanelService.getConfigurationInstanceFromConfigClass(reportSection);
    if (configurationInstance) {
      configurationInstance.availableDataSections = reportSection.config.availableDataSections.map(section => {
        // When allowUnselectAll is true and all subsections are unchecked, uncheck the parent section
        if (section.allowUnselectAll && section.subSections) {
          const allSubsectionsUnchecked = section.subSections.every(subsection => !subsection.isSelected);
          if (allSubsectionsUnchecked) {
            return { ...section, isSelected: false };
          }
        }
        return section;
      });
      configurationInstance.additionalConfiguration = reportSection.config.additionalConfiguration;
    }
    return configurationInstance;
  }

  public static GetUpdatedSizes(reportSection: ReportSectionConfigurationWithOrder): {
    minSize: number;
    maxSize: number;
  } {
    const config = ConfigurationPanelService.getUpdatedConfiguration(reportSection);
    let minSize: number;
    let maxSize: number;

    try {
      minSize = config?.minSize || reportSection.config.minSize;
    } catch (e) {
      minSize = reportSection.config.minSize;
    }

    try {
      maxSize = config?.maxSize || reportSection.config.maxSize;
    } catch (e) {
      maxSize = reportSection.config.maxSize;
    }

    return {
      minSize: minSize,
      maxSize: maxSize
    };
  }
  private static UpdateDataSectionsSelection(
    dataSection: ReportSectionConfigForDraggableItem,
    checked: boolean,
    reportSection: ReportSectionConfigurationWithOrder,
    maxGridUnitsAllowed: number | undefined
  ) {
    return reportSection.config.availableDataSections.reduce(
      (acc, section: ReportSectionDataSection) => {
        const isTargetSection = section.name === dataSection.name;
        let targetSection;
        if (isTargetSection) {
          targetSection = section;
        }
        let updatedSubSection = section.subSections;
        let sectionSelected = section.isSelected;

        // If this is not a parent item, update the sub-sections
        if (!dataSection.isParentItem) {
          updatedSubSection = section.subSections?.map(subSection => {
            const isTargetSubSection = subSection.name === dataSection.name;

            // In allowUnselectAll mode, we don't need to check if it's the last item in the group
            if (section.allowUnselectAll) {
              return {
                ...subSection,
                isSelected: isTargetSubSection ? checked : subSection.isSelected
              };
            }

            // Only enforce groupName restrictions if allowUnselectAll is false
            const otherGroupItems = section.subSections?.filter(
              sub => sub.isSelected && sub.groupName === subSection.groupName && sub.name !== subSection.name
            );
            const isLastSelected = otherGroupItems && otherGroupItems.length === 0 && subSection.isSelected;

            return {
              ...subSection,
              isSelected: isTargetSubSection ? (isLastSelected ? true : checked) : subSection.isSelected
            };
          }); // If all subsections are unchecked and allowUnselectAll is true, uncheck the parent section
          if (section.allowUnselectAll) {
            const allSubsectionsUnchecked = updatedSubSection?.every(subsection => !subsection.isSelected);
            if (allSubsectionsUnchecked) {
              sectionSelected = false;
            }
          }
        }

        // Push the updated section to the accumulator
        acc.dataSections.push({
          ...section,
          isSelected: isTargetSection ? checked : sectionSelected,
          subSections: updatedSubSection
        });

        if (targetSection && targetSection?.unit && section.unit && maxGridUnitsAllowed) {
          acc.targetSection = targetSection;
          acc.canCheckAllDataSections = ConfigurationPanelService.CanUpdateDataSectionUnit(
            targetSection,
            reportSection,
            maxGridUnitsAllowed,
            checked
          );
        }

        return acc;
      },
      {
        dataSections: [] as ReportSectionDataSection[],
        canCheckAllDataSections: true,
        targetSection: null as ReportSectionDataSection | null
      }
    );
  }

  public static DisableDataSectionsIfMaxGridUnitsExceeds(
    dataSelectionsInfo: {
      dataSections: ReportSectionDataSection[];
      canCheckAllDataSections: boolean;
      targetSection: ReportSectionDataSection | null;
    },
    maxGridUnitsAllowed: number | undefined
  ): ReportSectionDataSection[] {
    const { canCheckAllDataSections, dataSections, targetSection } = dataSelectionsInfo;

    if (maxGridUnitsAllowed && targetSection && targetSection?.unit) {
      return dataSections.map(section => {
        if (section.unit && !section.isSelected) {
          return {
            ...section,
            canConfigure: canCheckAllDataSections // disable data points which are not selected when sum exceeds maxGridUnitsAllowed
          };
        }
        return section;
      });
    }
    return dataSections;
  }

  public static UpdateReportSectionConfigOnDataSectionToggle(
    dataSection: ReportSectionConfigForDraggableItem,
    checked: boolean,
    reportSection: ReportSectionConfigurationWithOrder
  ): ReportSectionConfigurationWithOrder {
    const maxGridUnitsAllowed = reportSection.config.maxGridUnitsAllowed;
    const UpdateDataSectionsSelection = ConfigurationPanelService.UpdateDataSectionsSelection(
      dataSection,
      checked,
      reportSection,
      maxGridUnitsAllowed
    );

    const updatedAvailableDataSections = ConfigurationPanelService.DisableDataSectionsIfMaxGridUnitsExceeds(
      UpdateDataSectionsSelection,
      maxGridUnitsAllowed
    );

    const updatedSelectedDataSectionsCount = updatedAvailableDataSections.reduce(
      (count, section) => count + (section.isSelected ? 1 : 0),
      0
    );

    const updatedReportSection = {
      ...reportSection,
      isSelected: updatedSelectedDataSectionsCount < 1 ? false : reportSection.isSelected,
      config: {
        ...reportSection.config,
        availableDataSections: updatedAvailableDataSections,
        selectedDataSectionsCount: updatedSelectedDataSectionsCount
      } as unknown as ReportSectionConfigurationBase
    };

    const { maxSize, minSize } = ConfigurationPanelService.GetUpdatedSizes(updatedReportSection);
    updatedReportSection.config.minSize = minSize;
    updatedReportSection.config.maxSize = maxSize;

    return updatedReportSection;
  }
}

export function getTemplateDesignerSelectedReportState(
  reportSectionConfig: ReportSectionConfigurationWithOrder | undefined
): TemplateDesignerSelectedReportState | null {
  if (reportSectionConfig) {
    return {
      reportSectionConfig: reportSectionConfig,
      reportSectionConfigDraggableItem: ConfigurationPanelService.PrepareDraggableItemsConfig(reportSectionConfig)
    };
  }
  return null;
}
